import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Phone, Mail, MapPin, Calendar, CheckCircle, Clock, AlertCircle, Search, Filter, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  nouveau: { label: "Nouveau", color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertCircle },
  traite: { label: "Traité", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  en_cours: { label: "En cours", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Clock },
};

const AdminPanel = () => {
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({ total: 0, nouveau: 0, traite: 0 });
  const [loading, setLoading] = useState(true);
  const [filterRegion, setFilterRegion] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [adminKey, setAdminKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");

  const adminHeaders = { "X-Admin-Key": adminKey };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts/count`, { headers: { "X-Admin-Key": adminKey } });
      if (res.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem("bkr_admin_key", adminKey);
      } else {
        setAuthError("Clé admin invalide");
      }
    } catch {
      setAuthError("Erreur de connexion");
    }
  };

  useEffect(() => {
    const savedKey = sessionStorage.getItem("bkr_admin_key");
    if (savedKey) {
      setAdminKey(savedKey);
      setIsAuthenticated(true);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRegion) params.append("region", filterRegion);
      if (filterStatus) params.append("status", filterStatus);
      const url = `${API_URL}/api/admin/contacts${params.toString() ? `?${params}` : ""}`;
      const res = await fetch(url, { headers: adminHeaders });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    }
    setLoading(false);
  }, [filterRegion, filterStatus, isAuthenticated, adminKey]);

  const fetchCounts = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts/count`, { headers: adminHeaders });
      if (res.ok) {
        const data = await res.json();
        setCounts(data);
      }
    } catch (err) {
      console.error("Failed to fetch counts:", err);
    }
  };

  useEffect(() => {
    document.title = "Admin - Demandes de contact | BKR Toiture";
    if (isAuthenticated) {
      fetchContacts();
      fetchCounts();
    }
  }, [fetchContacts, isAuthenticated]);

  const updateStatus = async (contactId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/contacts/${contactId}?status=${newStatus}`, { method: "PATCH", headers: adminHeaders });
      fetchContacts();
      fetchCounts();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const filteredContacts = contacts.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.phone || "").toLowerCase().includes(term) ||
      (c.city || "").toLowerCase().includes(term) ||
      (c.message || "").toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black uppercase tracking-tight font-['Barlow_Condensed'] text-white">
              Administration
            </h1>
            <p className="text-white/50 text-sm mt-2">BKR Toiture — Espace sécurisé</p>
          </div>
          <form onSubmit={handleLogin} className="bg-white p-8 shadow-lg space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1">Clé d'administration</label>
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Entrez votre clé admin..."
                className="w-full border border-[#E2E8F0] px-4 py-3 text-sm text-[#0F172A] focus:outline-none focus:border-[#C2410C]"
                data-testid="admin-key-input"
                autoFocus
              />
            </div>
            {authError && (
              <p className="text-red-600 text-sm font-medium" data-testid="admin-auth-error">{authError}</p>
            )}
            <Button
              type="submit"
              className="w-full h-12 bg-[#C2410C] hover:bg-[#9A3412] uppercase font-bold tracking-wider text-white"
              data-testid="admin-login-btn"
            >
              Accéder
            </Button>
            <Link to="/" className="block text-center text-sm text-[#64748B] hover:text-[#C2410C] transition-colors mt-4">
              Retour au site
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/50 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-black uppercase tracking-tight font-['Barlow_Condensed']" data-testid="admin-title">
                  Administration
                </h1>
                <p className="text-white/50 text-sm">Gestion des demandes de contact</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => { fetchContacts(); fetchCounts(); }}
                className="bg-[#C2410C] hover:bg-[#9A3412] h-10 px-4 text-xs uppercase font-bold tracking-wider"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <Button
                onClick={() => { sessionStorage.removeItem("bkr_admin_key"); setIsAuthenticated(false); setAdminKey(""); }}
                variant="outline"
                className="h-10 px-4 text-xs uppercase font-bold tracking-wider text-white/60 border-white/20 hover:bg-white/10 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Quitter
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-stats">
          <div className="bg-white p-6 shadow-sm border-l-4 border-[#0F172A]">
            <p className="text-sm text-[#64748B] uppercase tracking-wider font-medium">Total demandes</p>
            <p className="text-3xl font-black text-[#0F172A] font-['Barlow_Condensed'] mt-1" data-testid="stat-total">{counts.total}</p>
          </div>
          <div className="bg-white p-6 shadow-sm border-l-4 border-amber-500">
            <p className="text-sm text-[#64748B] uppercase tracking-wider font-medium">Nouvelles</p>
            <p className="text-3xl font-black text-amber-600 font-['Barlow_Condensed'] mt-1" data-testid="stat-nouveau">{counts.nouveau}</p>
          </div>
          <div className="bg-white p-6 shadow-sm border-l-4 border-emerald-500">
            <p className="text-sm text-[#64748B] uppercase tracking-wider font-medium">Traitées</p>
            <p className="text-3xl font-black text-emerald-600 font-['Barlow_Condensed'] mt-1" data-testid="stat-traite">{counts.traite}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 shadow-sm flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, ville..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 border-0 outline-none text-sm text-[#0F172A] placeholder:text-[#94A3B8] bg-transparent"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Filter className="w-4 h-4 text-[#94A3B8]" />
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="text-sm border border-[#E2E8F0] px-3 py-2 text-[#334155] bg-white"
              data-testid="filter-region"
            >
              <option value="">Toutes régions</option>
              <option value="yvelines">Yvelines (78)</option>
              <option value="haute-savoie">Haute-Savoie (74)</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm border border-[#E2E8F0] px-3 py-2 text-[#334155] bg-white"
              data-testid="filter-status"
            >
              <option value="">Tous statuts</option>
              <option value="nouveau">Nouveau</option>
              <option value="en_cours">En cours</option>
              <option value="traite">Traité</option>
            </select>
          </div>
        </div>

        {/* Contact List */}
        <div className="space-y-3" data-testid="contacts-list">
          {loading ? (
            <div className="bg-white p-12 text-center text-[#94A3B8]">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" />
              <p>Chargement des demandes...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="bg-white p-12 text-center text-[#94A3B8]" data-testid="no-contacts">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Aucune demande de contact</p>
              <p className="text-sm mt-1">Les demandes du formulaire apparaîtront ici.</p>
            </div>
          ) : (
            filteredContacts.map((contact) => {
              const status = statusConfig[contact.status] || statusConfig.nouveau;
              const StatusIcon = status.icon;
              const isExpanded = expandedId === contact.id;

              return (
                <div
                  key={contact.id}
                  className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  data-testid={`contact-card-${contact.id}`}
                  onClick={() => setExpandedId(isExpanded ? null : contact.id)}
                >
                  {/* Summary Row */}
                  <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-[#0F172A] truncate">{contact.name}</h3>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border font-medium ${status.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{contact.phone}</span>
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{contact.email}</span>
                        {contact.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{contact.city}</span>}
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(contact.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {contact.status !== "en_cours" && (
                        <Button
                          onClick={() => updateStatus(contact.id, "en_cours")}
                          variant="outline"
                          className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                          data-testid={`status-encours-${contact.id}`}
                        >
                          En cours
                        </Button>
                      )}
                      {contact.status !== "traite" && (
                        <Button
                          onClick={() => updateStatus(contact.id, "traite")}
                          className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`status-traite-${contact.id}`}
                        >
                          Traité
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-[#E2E8F0] p-4 md:p-5 bg-[#F8FAFC] space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-[#64748B]">Service demandé :</span>
                          <p className="text-[#0F172A] font-medium mt-0.5">{contact.service || "-"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-[#64748B]">Région :</span>
                          <p className="text-[#0F172A] font-medium mt-0.5 capitalize">{contact.region || "-"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-[#64748B]">Adresse chantier :</span>
                          <p className="text-[#0F172A] mt-0.5">{contact.address || "-"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-[#64748B]">Téléphone :</span>
                          <p className="text-[#0F172A] mt-0.5">
                            <a href={`tel:${contact.phone}`} className="text-[#C2410C] hover:underline">{contact.phone}</a>
                          </p>
                        </div>
                      </div>
                      {contact.message && (
                        <div className="text-sm">
                          <span className="font-medium text-[#64748B]">Message :</span>
                          <p className="text-[#0F172A] mt-1 bg-white p-3 border border-[#E2E8F0]">{contact.message}</p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <a href={`tel:${contact.phone}`}>
                          <Button className="h-9 px-4 text-xs bg-[#C2410C] hover:bg-[#9A3412] text-white uppercase font-bold tracking-wider">
                            <Phone className="w-3 h-3 mr-1" /> Appeler
                          </Button>
                        </a>
                        <a href={`mailto:${contact.email}`}>
                          <Button variant="outline" className="h-9 px-4 text-xs uppercase font-bold tracking-wider border-[#E2E8F0]">
                            <Mail className="w-3 h-3 mr-1" /> Email
                          </Button>
                        </a>
                        <a href={`https://wa.me/${(contact.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" className="h-9 px-4 text-xs uppercase font-bold tracking-wider border-[#E2E8F0] text-[#25D366]">
                            WhatsApp
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
